import React, {useMemo, useRef, useState} from "react";
import {useDispatch} from "react-redux";
import {useHistory} from "react-router-dom";
import PropTypes from "prop-types";

//components:
import {makeStyles} from "@material-ui/core/styles";
import {FormGroup, Table, TableBody, TableCell, TableFooter, TableRow} from "@material-ui/core";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import {Dropdown} from "react-bootstrap";
import SweetAlert from "react-bootstrap-sweetalert";
import {CustomSwitch, CustomTooltip, EnhancedTableHead, ImageUploadModal, PaginationTable} from "../../../widgets";
import Swal from "sweetalert2";

//helpers:
//actions:
import {deleteTrack, editTrack, updateFolderImgTrack, updateHeaderImgTrack} from "../../../redux/Settings/tracksCrud";
import {settingsActions} from "../../../redux/Settings/settingsRedux";
import {tracksActions} from "../../../redux/Tracks/tracksRedux";

//styles:
const tracksListStyles = makeStyles(theme => ({
    root: {
        width: "100%",
    },
    table: {
        minWidth: 500,
    },
}));

const TracksTable = ({t, tracks}) => {
    const classes = tracksListStyles();
    let history = useHistory();

    //redux:
    //actions:
    const dispatch = useDispatch();
    const getTracks = () => {
        dispatch(settingsActions.getSettingTracks());
        dispatch(tracksActions.getNormalTracks());
    };
    //local state:
    const [order, setOrder] = useState("asc");
    const [orderBy, setOrderBy] = useState("title");
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const [showEditTitleInput, setShowEditTitleInput] = useState("");
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [trackID, setTrackID] = useState("");
    const [openUploadImageModal, setOpenUploadImageModal] = useState({
        open: false,
        type: "",
    });
    const [placeholderUploadImg, setPlaceholderUploadImg] = useState("");

    //refs:
    const tableRef = useRef(null);

    const headRows = [
        {
            id: "name",
            align: "left",
            disablePadding: false,
            label: t("TABLE.HEADER.NAME"),
        },
        {
            id: "videos",
            align: "left",
            disablePadding: false,
            label: t("TABLE.HEADER.NUM_VIDEOS"),
        },
        {
            id: "visibleTitle",
            align: "left",
            disablePadding: false,
            label: t("ADMINPANEL.TRACKS.TABLE.HEADER.CARD_TITLE"),
        },
        {
            id: "showHighlights",
            align: "left",
            disablePadding: false,
            label: t("ADMINPANEL.TRACKS.TABLE.HEADER.SHOW_HIGHLIGHTS"),
        },
        {
            id: "home_pic",
            align: "left",
            disablePadding: false,
            label: t("ADMINPANEL.TRACKS.TABLE.HEADER.CARD_PIC"),
        },
        {
            id: "header_pic",
            align: "left",
            disablePadding: false,
            label: t("ADMINPANEL.TRACKS.TABLE.HEADER.HEADER_PIC"),
        },
        {
            id: "actions",
            align: "left",
            disablePadding: false,
            label: t("TABLE.HEADER.ACTIONS"),
        },
    ];
    const onWheel = e => {
        e.preventDefault();
        if (window.innerWidth < 1750) {
            const container = tableRef.current;
            const containerScrollPosition = tableRef.current.scrollLeft;

            container.scrollTo({
                top: 0,
                left: containerScrollPosition + e.deltaY,
            });
        }
    };

    const desc = (a, b, orderBy) => {
        const aLowerCase = (a[orderBy] || "").toString().toLowerCase();
        const bLowerCase = (b[orderBy] || "").toString().toLowerCase();

        if (bLowerCase < aLowerCase) {
            return -1;
        }
        if (bLowerCase > aLowerCase) {
            return 1;
        }
        return 0;
    };

    const stableSort = (array, cmp) => {
        const stabilizedThis = array.map((el, index) => [el, index]);
        stabilizedThis.sort((a, b) => {
            const order = cmp(a[0], b[0]);
            if (order !== 0) return order;
            return a[1] - b[1];
        });
        const stableSorted = stabilizedThis.map(el => el[0]);
        return stableSorted;
    };

    const getSorting = (order, orderBy) => {
        return order === "desc" ? (a, b) => desc(a, b, orderBy) : (a, b) => -desc(a, b, orderBy);
    };

    const handleRequestSort = (event, property) => {
        const isDesc = orderBy === property && order === "desc";
        setOrder(isDesc ? "asc" : "desc");
        setOrderBy(property);
    };

    const handleVideoClick = id => {
        history.push(`/media/${id}`);
    };

    const onTrackChange = (value, key, trackId) => {
        editTrack(value, key, trackId)
            .then(() => {
                getTracks();
            })
            .catch(err => {
                Swal.fire({
                    icon: "error",
                    title: t("MODAL.EDIT.TRACK.FAIL"),
                    text: err.message,
                });
            });
    };

    const deleteIt = () => {
        deleteTrack(trackID)
            .then(() => {
                Swal.fire({
                    icon: "success",
                    title: t("MODAL.CONFIRM_DELETE.TRACK.SUCCESS"),
                });
                getTracks();
            })
            .catch(err => {
                Swal.fire({
                    icon: "error",
                    title: t("MODAL.CONFIRM_DELETE.TRACK.FAIL"),
                    text: err.message,
                });
            });

        setShowDeleteModal(false);
        setTrackID("");
    };

    const uploadImageHandler = ({id, file, picType, onConfirmUploaded, onError}) => {
        if (file && picType) {
            const onSuccess = () => {
                getTracks();
                onConfirmUploaded();
            };

            if (picType === "picture") updateFolderImgTrack({id, file, onSuccess, onError});
            else if (picType === "header") updateHeaderImgTrack({id, file, onSuccess, onError});
        }
    };

    let table = useMemo(
        () =>
            tracks?.length ? (
                <div className={`${classes.root}`}>
                    <div className={`custom-scroll-area horizontal-scroll`} onWheel={onWheel} ref={tableRef}>
                        <Table className={classes.table}>
                            <EnhancedTableHead
                                order={order}
                                orderBy={orderBy}
                                onRequestSort={handleRequestSort}
                                rowCount={tracks && tracks.length}
                                headRows={headRows}
                            />
                            <TableBody>
                                {tracks &&
                                stableSort(tracks, getSorting(order, orderBy))
                                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                    .map(track => (
                                        <TableRow key={track.track_id}>
                                            {/* Column1: name */}
                                            <TableCell align="left" component="th" scope="row">
                                                <div className="d-flex align-items-center">
                                                    <div className="ml-4 position-relative w-150px d-flex flex-wrap">
                                                        {showEditTitleInput !== track?.track_id && (
                                                            <div
                                                                className={`${
                                                                    showEditTitleInput === track?.track_id
                                                                        ? "d-none"
                                                                        : "d-flex"
                                                                } text-dark-75 font-size-base w-fit-content py-2 px-4`}
                                                            >
                                                                {track?.name}
                                                                <CustomTooltip
                                                                    config={{
                                                                        text: t("TOOLTIP.EDIT_NAME"),
                                                                        placement: "top",
                                                                    }}
                                                                >
                                                                    <button
                                                                        onClick={() => {
                                                                            setShowEditTitleInput(track?.track_id);
                                                                        }}
                                                                        className={`btn btn-icon btn-light ml-2 edit_button ${showEditTitleInput &&
                                                                        "d-none"}`}
                                                                    >
                                                                        <EditIcon />
                                                                    </button>
                                                                </CustomTooltip>
                                                            </div>
                                                        )}
                                                        {showEditTitleInput === track?.track_id && (
                                                            <input
                                                                type="text"
                                                                autoFocus
                                                                className={`form-control form-control-solid h-auto py-2 px-4 text-dark-75 font-size-base w-150px`}
                                                                defaultValue={track?.name}
                                                                onKeyDown={e => {
                                                                    const value = e.target.value.replace(
                                                                        /\s\s+/g,
                                                                        " ",
                                                                    );

                                                                    if (e.key === "Enter") {
                                                                        const trimmed = value.trim();
                                                                        if (trimmed) {
                                                                            trimmed !== track.name &&
                                                                            onTrackChange(
                                                                                trimmed,
                                                                                "name",
                                                                                track?.track_id,
                                                                            );
                                                                        }
                                                                        setShowEditTitleInput("");
                                                                        e.target.value = trimmed;
                                                                    } else e.target.value = value;
                                                                }}
                                                                onBlur={e => {
                                                                    const name = e.target.value.trim();
                                                                    if (name) {
                                                                        name !== track.name &&
                                                                        onTrackChange(
                                                                            name,
                                                                            "name",
                                                                            track?.track_id,
                                                                        );
                                                                    }
                                                                    setShowEditTitleInput("");
                                                                    e.target.value = name;
                                                                }}
                                                            />
                                                        )}
                                                    </div>
                                                </div>
                                            </TableCell>

                                            {/* Column2: # of videos: */}
                                            <TableCell align="left" className="text-muted mb-0 font-size-1rem">
                                                <div className="d-flex align-items-center">
                                                    <Dropdown>
                                                        <CustomTooltip
                                                            config={{
                                                                text: t("TOOLTIP.SEE_VIDEOS"),
                                                                placement: "top",
                                                            }}
                                                        >
                                                            <Dropdown.Toggle variant="light">
                                                                {(track?.videos && track?.videos.length ? track?.videos.filter(video => video.status?.accessible === 1).length : null) ?? 0}
                                                            </Dropdown.Toggle>
                                                        </CustomTooltip>

                                                        <Dropdown.Menu className="tables_dropdown_list">
                                                            {track?.videos.length ? (
                                                                track?.videos.map(video => (
                                                                    <Dropdown.Item
                                                                        key={video.id}
                                                                        className="dropdown-item elipsis_1line"
                                                                        onClick={() => handleVideoClick(video.id)}
                                                                    >
                                                                        {video.title}
                                                                    </Dropdown.Item>
                                                                ))
                                                            ) : (
                                                                <span>
                                                                        {t("ADMINPANEL.TRACKS.TABLE.VIDEOS_EMPTY")}
                                                                    </span>
                                                            )}
                                                        </Dropdown.Menu>
                                                    </Dropdown>
                                                </div>
                                            </TableCell>

                                            {/* Column3: card title: */}
                                            <TableCell align="left" className="text-muted mb-0 font-size-1rem">
                                                <FormGroup className="d-flex flex-row flex-wrap align-items-center">
                                                    <CustomTooltip
                                                        config={{
                                                            text: t("TOOLTIP.TRACKS.DISPLAY_TITLE"),
                                                            placement: "top",
                                                        }}
                                                    >
                                                        <div className="d-flex">
                                                            <CustomSwitch
                                                                size="small"
                                                                checked={!!track?.visibleTitle}
                                                                onChange={e =>
                                                                    onTrackChange(
                                                                        e.target.checked ? 1 : 0,
                                                                        "visibleTitle",
                                                                        track?.track_id,
                                                                    )
                                                                }
                                                            />
                                                        </div>
                                                    </CustomTooltip>
                                                </FormGroup>
                                            </TableCell>

                                            {/* Column4: card title: */}
                                            <TableCell align="left" className="text-muted mb-0 font-size-1rem">
                                                <FormGroup className="d-flex flex-row flex-wrap align-items-center">
                                                    <CustomTooltip
                                                        config={{
                                                            text: t("TOOLTIP.TRACKS.DISPLAY_HIGHLIGHT_NETGRAPH"),
                                                            placement: "top",
                                                        }}
                                                    >
                                                        <div className="d-flex">
                                                            <CustomSwitch
                                                                size="small"
                                                                checked={!!track?.showHighlights}
                                                                onChange={e =>
                                                                    onTrackChange(
                                                                        e.target.checked ? 1 : 0,
                                                                        "showHighlights",
                                                                        track?.track_id,
                                                                    )
                                                                }
                                                            />
                                                        </div>
                                                    </CustomTooltip>
                                                </FormGroup>
                                            </TableCell>

                                            {/* Column5: card pic: */}
                                            <TableCell align="left" className="text-muted mb-0 font-size-1rem">
                                                <div className="d-flex position-relative w-50px">
                                                        <span
                                                            className="bg_image_cover rounded w-50px h-50px"
                                                            style={{
                                                                backgroundImage: `url(${track?.picture}`,
                                                            }}
                                                        />
                                                    <CustomTooltip
                                                        config={{
                                                            text: t("TOOLTIP.EDIT_CARD_IMG"),
                                                            placement: "top",
                                                        }}
                                                    >
                                                        <button
                                                            onClick={() => {
                                                                setOpenUploadImageModal({
                                                                    open: true,
                                                                    type: "picture",
                                                                });
                                                                setTrackID(track?.track_id);
                                                                setPlaceholderUploadImg(track?.picture);
                                                            }}
                                                            className={`btn btn-icon btn-light position-absolute right-0 bottom-0 edit_button ${showEditTitleInput &&
                                                            "d-none"}`}
                                                        >
                                                            <EditIcon />
                                                        </button>
                                                    </CustomTooltip>
                                                </div>
                                            </TableCell>

                                            {/* Column6: header pic: */}
                                            <TableCell align="left">
                                                <div className="d-flex position-relative" style={{width: 95}}>
                                                        <span
                                                            className="bg_image_cover rounded h-50px w-100"
                                                            style={{
                                                                backgroundImage: `url(${track?.header})`,
                                                            }}
                                                        />
                                                    <CustomTooltip
                                                        config={{
                                                            text: t("TOOLTIP.EDIT.HEADER_IMG"),
                                                            placement: "top",
                                                        }}
                                                    >
                                                        <button
                                                            onClick={() => {
                                                                setOpenUploadImageModal({
                                                                    open: true,
                                                                    type: "header",
                                                                });
                                                                setTrackID(track?.track_id);
                                                                setPlaceholderUploadImg(track?.header);
                                                            }}
                                                            className={`btn btn-icon btn-light position-absolute right-0 bottom-0 edit_button ${showEditTitleInput &&
                                                            "d-none"}`}
                                                        >
                                                            <EditIcon />
                                                        </button>
                                                    </CustomTooltip>
                                                </div>
                                            </TableCell>

                                            {/*Column7: actions: */}
                                            <TableCell align="left">
                                                <div className="d-flex align-items-center justify-content-between">
                                                    {/* switcher: */}
                                                    <FormGroup className="d-flex flex-row flex-wrap align-items-center">
                                                        <CustomTooltip
                                                            config={{
                                                                text: t("TOOLTIP.SHOW_HIDE"),
                                                                placement: "top",
                                                            }}
                                                        >
                                                            <div className="d-flex">
                                                                <CustomSwitch
                                                                    size="small"
                                                                    checked={!!track?.visible}
                                                                    onChange={e =>
                                                                        onTrackChange(
                                                                            e.target.checked ? 1 : 0,
                                                                            "visible",
                                                                            track?.track_id,
                                                                        )
                                                                    }
                                                                />
                                                            </div>
                                                        </CustomTooltip>
                                                    </FormGroup>
                                                    {/* delete button: */}
                                                    <CustomTooltip
                                                        config={{
                                                            text: t("TOOLTIP.DELETE.TRACK"),
                                                            placement: "top",
                                                        }}
                                                    >
                                                        <button
                                                            className="btn btn-icon btn-light btn-hover-primary btn-sm button_blue_icon ml-3"
                                                            disabled={track?.isSpecial}
                                                            onClick={() => {
                                                                setTrackID(track?.track_id);
                                                                setShowDeleteModal(true);
                                                            }}
                                                        >
                                                            <DeleteIcon />
                                                        </button>
                                                    </CustomTooltip>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))}

                                <TableRow style={{height: 48}}>
                                    <TableCell colSpan={6} />
                                </TableRow>
                            </TableBody>

                            {/* pagination: */}
                            <TableFooter>
                                <TableRow>
                                    <PaginationTable
                                        setPage={setPage}
                                        setRowsPerPage={setRowsPerPage}
                                        rows={tracks}
                                        rowsPerPage={rowsPerPage}
                                        page={page}
                                        rowsPerPageOptions={[5, 10, 25]}
                                        variant={"filled"}
                                    />
                                </TableRow>
                            </TableFooter>
                        </Table>
                    </div>

                    <SweetAlert
                        show={showDeleteModal}
                        warning
                        title={t("MODAL.CONFIRM_DELETE.TITLE")}
                        showCancel
                        confirmBtnText={t("MODAL.CONFIRM_DELETE.CONFIRM_BTN")}
                        confirmBtnBsStyle="danger"
                        focusCancelBtn
                        onConfirm={deleteIt}
                        onCancel={() => {
                            setShowDeleteModal(false);
                            setTrackID("");
                        }}
                    >
                        {t("MODAL.CONFIRM_DELETE.DESCRIPTION")}
                    </SweetAlert>

                    {/* upload image: */}
                    <ImageUploadModal
                        id={`${trackID}`}
                        onUpload={uploadImageHandler}
                        open={openUploadImageModal}
                        setOpen={setOpenUploadImageModal}
                        placeholder={placeholderUploadImg}
                    />
                </div>
            ) : (
                <div className="alert alert-light text-dark w-fit-content">
                    {t("ADMINPANEL.TRACKS.TABLE.EMPTY_MESSAGE")}
                </div>
            ),
        [tracks, showEditTitleInput, showDeleteModal, trackID, openUploadImageModal, page, rowsPerPage, order, orderBy],
    );

    return table;
};

TracksTable.propTypes = {
    tracks: PropTypes.arrayOf(PropTypes.object),
};

export default TracksTable;
